import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { palette } from 'styled-theme';
import { device } from '@jjj/components';
import { transition } from '../../../settings/style-util';

export const OrgSwitcherWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${palette('grayscale', 5)};
  width: 248px;
  min-width: 220px;
  border-radius: 5px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.14), 2px 2px 10px 0px rgba(0, 0, 0, 0.06);
  margin: -12px -16px;
`;

export const OrgsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 10px;
  border-bottom: 1px solid ${palette('border', 0)};
`;

export const Header = styled.div`
  padding: 0 10px;

  h3 {
    font-size: 14px;
    font-weight: 700;
    color: ${palette('grayscale', 0)};
    margin: 0;
    letter-spacing: 0.24px;
    line-height: 1.5;
  }
`;

export const OrgsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 3px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${palette('grayscale', 1)};
    border-radius: 100px;
    border: 2.5px solid transparent;
    background-clip: padding-box;
  }
`;

export const OrgItem = styled.div<{ $isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$isExpanded ? '4px' : '0')};
`;

export const OrgHeader = styled.button<{ $isExpanded?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 10px;
  height: 32px;
  gap: 8px;
  background: ${props => (props.$isExpanded ? palette('grayscale', 9)(props) : 'transparent')};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  ${transition()};

  &:hover {
    background-color: ${palette('grayscale', 9)};
  }
`;

export const OrgLogo = styled.div<{ $logoUrl?: string | null }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${palette('border', 0)};
  background-image: ${props => (props.$logoUrl ? `url(${props.$logoUrl})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

export const OrgName = styled.span`
  flex: 1;
  text-align: left;
  font-size: 13px;
  font-weight: 400;
  color: ${palette('grayscale', 0)};
  letter-spacing: 0.24px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChevronIcon = styled.i<{ $isExpanded?: boolean }>`
  width: 16px;
  height: 16px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${palette('grayscale', 7)};
  transform: ${props => (props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  ${transition()};
`;

export const RolesContainer = styled.div<{ $isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  max-height: ${props => (props.$isExpanded ? '200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-out;
`;

export const RoleItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 38px;
  padding-right: 10px;
  height: 32px;
  border-radius: 4px;
  ${transition()};

  &:hover {
    background-color: ${palette('grayscale', 9)};
  }
`;

export const RoleName = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${palette('grayscale', 0)};
  letter-spacing: 0.24px;
  line-height: 1.5;
`;

export const RoleStatus = styled.button<{ $isCurrent?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  color: ${palette('primary', 0)};
  opacity: ${props => (props.$isCurrent ? 0.5 : 1)};
  text-decoration: ${props => (props.$isCurrent ? 'none' : 'underline')};
  cursor: ${props => (props.$isCurrent ? 'default' : 'pointer')};
  letter-spacing: 0.24px;
  line-height: 1.5;
`;

export const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 16px 10px;
`;

const actionItemStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 10px;
  height: 32px;
  gap: 8px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background-color: ${palette('grayscale', 9)};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${palette('grayscale', 0)};
    flex-shrink: 0;
  }

  span {
    font-size: 13px;
    font-weight: 400;
    color: ${palette('grayscale', 0)};
    letter-spacing: 0.24px;
    line-height: 1.5;
  }
`;

export const ActionItem = styled.button`
  ${actionItemStyles}
  ${transition()};
`;

export const StyledLink = styled(Link)`
  ${actionItemStyles}
  ${transition()};
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

export const UserTitle = styled.span`
  font-size: 13px;
  font-weight: 900;
  color: ${palette('grayscale', 0)};
  font-family: Helvetica;
  display: flex;
  align-items: center;

  .user {
    display: none;

    @media ${device.tablet} {
      display: block;
    }
  }
`;

export const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;

  & i {
    font-size: 10px !important;
  }
`;

export const UserAvatarWrapper = styled.div<{ avatar?: string }>`
  background-image: url(${props => props.avatar});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 50% !important;
  height: 24px;
  width: 24px;
`;
